import { getDeliveryReceiptTemplate } from './deliveryReceiptTemplate';
import { COUNTRIES_REGIONAL_CONFIG } from '@wareclouds/frontend-common';
import { CONTEXT } from '../../../config/consts/commons';

const { countryCode = 'es-CL' } = COUNTRIES_REGIONAL_CONFIG[CONTEXT] || {};

const W = 1224;
const PADDING_X = 64;
const JPEG_QUALITY = 0.7;

const COLOR_BLACK = '#000000';
const COLOR_BORDER = '#cccccc';
const COLOR_MUTED = '#999999';

const FONT_FAMILY = '"Helvetica Neue", Arial, sans-serif';
const FONT_LABEL = `400 16px ${FONT_FAMILY}`;
const FONT_VALUE = `400 13px ${FONT_FAMILY}`;
const LINE_HEIGHT = 1.6;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(countryCode, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString(countryCode, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function canvasToBlob(canvas, mimeType, quality, onCleanup) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (onCleanup) onCleanup();
        canvas.width = 0;

        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate receipt blob'));
        }
      },
      mimeType,
      quality
    );
  });
}

function drawDataField(
  ctx,
  { label, value, x, y, maxWidth, inline = false, na = 'N/D' }
) {
  const displayValue = value || na;

  if (inline) {
    ctx.font = FONT_LABEL;
    ctx.fillStyle = COLOR_BLACK;
    ctx.textAlign = 'left';
    const labelText = label.toUpperCase() + ': ';
    ctx.fillText(labelText, x, y);
    const labelWidth = ctx.measureText(labelText).width;
    ctx.font = FONT_VALUE;
    ctx.fillText(displayValue, x + labelWidth, y);
    return y + 16 * LINE_HEIGHT + 20;
  }

  ctx.font = FONT_LABEL;
  ctx.fillStyle = COLOR_BLACK;
  ctx.textAlign = 'left';
  ctx.fillText(label.toUpperCase(), x, y);

  const valueY = y + 16 * LINE_HEIGHT + 6;
  ctx.font = FONT_VALUE;

  const words = displayValue.split(' ');
  let line = '';
  let lineY = valueY;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, lineY);
      line = word;
      lineY += 13 * LINE_HEIGHT;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, lineY);

  return lineY + 13 * LINE_HEIGHT + 20;
}

export default async function generateDeliveryReceiptImageNative(
  data,
  { t, localeKey = 'es' }
) {
  const {
    recipientName = '',
    recipientFirstName = '',
    recipientLastName = '',
    recipientAddress = '',
    receiverName = '',
    receiverId = '',
    relationship = '',
    sender = '',
    invoiceNumber = '-',
    shipment = '-',
    order: orderCode = '',
    boxes = '',
    issueDate = '',
    signatureDateTime = '',
    signatureDataUrl = null,
  } = data;

  const na = t('not_applicable', { ns: 'glossary' });

  const recipientNameFull =
    recipientName ||
    [recipientFirstName, recipientLastName].filter(Boolean).join(' ') ||
    na;

  const issueDateFormatted = formatDate(issueDate);
  const signatureDateTimeFormatted = formatDateTime(
    signatureDateTime || new Date().toISOString()
  );

  const [templateBitmap, signatureImg] = await Promise.all([
    getDeliveryReceiptTemplate(t, localeKey),
    signatureDataUrl
      ? loadImage(signatureDataUrl).catch(() => null)
      : Promise.resolve(null),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = 700;

  const ctx = canvas.getContext('2d');

  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  if (typeof ctx.setTransform === 'function') {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(templateBitmap, 0, 0);

  const logoTargetW = 520;
  const gridTop = 180;
  const colWidth = (W - PADDING_X * 2 - 48 * 2) / 3;
  const col1X = PADDING_X;
  const col2X = col1X + colWidth + 48;
  const col3X = col2X + colWidth + 48;
  const colMaxW = colWidth - 8;

  void logoTargetW;

  const field = (label, value, extra = {}) =>
    drawDataField(ctx, {
      label: t(label, { ns: 'glossary' }),
      value,
      x: extra.x ?? col1X,
      y: extra.y,
      maxWidth: extra.maxWidth ?? colMaxW,
      ...extra,
    });

  let y1 = gridTop + 40;
  y1 = field('recipient_name', recipientNameFull, { y: y1 });
  y1 = field('recipient_address', recipientAddress, { y: y1 });
  y1 = field('recipient_full_name', receiverName, { y: y1 });
  y1 = field('recipient_id', receiverId, { y: y1, inline: true });
  field('relationship', relationship, { y: y1 });

  const sigBoxX = col2X;
  const sigBoxY = gridTop + 40;
  const sigBoxW = 295;
  const sigBoxH = 193;

  if (signatureImg) {
    ctx.drawImage(signatureImg, sigBoxX, sigBoxY, sigBoxW, sigBoxH);
  } else {
    ctx.strokeStyle = COLOR_BORDER;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(sigBoxX, sigBoxY, sigBoxW, sigBoxH);
    ctx.setLineDash([]);
    ctx.font = `400 12px ${FONT_FAMILY}`;
    ctx.fillStyle = COLOR_MUTED;
    ctx.textAlign = 'center';
    ctx.fillText(
      t('signature', { ns: 'glossary' }),
      sigBoxX + sigBoxW / 2,
      sigBoxY + sigBoxH / 2
    );
  }

  let y3 = gridTop + 40;
  y3 = field('sender', sender, { x: col3X, y: y3 });
  y3 = field('invoice', invoiceNumber, { x: col3X, y: y3, inline: true });
  y3 = field('shipment', shipment, { x: col3X, y: y3, inline: true });
  y3 = field('order', orderCode, { x: col3X, y: y3, inline: true });
  y3 = field('boxes', String(boxes), { x: col3X, y: y3, inline: true });
  y3 = field('issue_date', issueDateFormatted, {
    x: col3X,
    y: y3,
    inline: true,
  });
  field('signature_date', signatureDateTimeFormatted, { x: col3X, y: y3 });

  // templateBitmap belongs to the singleton — must NOT be closed here
  return canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY, () => {
    if (signatureImg) signatureImg.src = '';
  });
}
